import {
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
  wrap,
} from 'motion/react';
import { useRef } from 'react';

export type AnimationDirection = -1 | 1;

export interface UseVelocityMarqueeOptions<T> {
  baseVelocity: number;
  mapProgress: (progress: number) => T;
  damping?: number;
  stiffness?: number;
  mapVelocityFactor?: [number, number];
}

const useVelocityMarquee = <T>({
  baseVelocity,
  mapProgress,
  damping = 50,
  stiffness = 400,
  mapVelocityFactor = [0, 100],
}: UseVelocityMarqueeOptions<T>) => {
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, { damping, stiffness });

  const velocityFactor = useTransform(
    smoothVelocity,
    [0, 100],
    mapVelocityFactor,
    {
      clamp: false,
    }
  );

  const x = useTransform(baseX, (value) => {
    const progress = wrap(0, 100, value);

    return mapProgress(progress);
  });

  const directionFactor = useRef<AnimationDirection>(1);

  useAnimationFrame((_, delta) => {
    const baseMove = directionFactor.current * baseVelocity * (delta / 1000);

    if (velocityFactor.get() < 0) {
      directionFactor.current = -1;
    } else if (velocityFactor.get() > 0) {
      directionFactor.current = 1;
    }

    const moveBy =
      baseMove + directionFactor.current * baseMove * velocityFactor.get();

    baseX.set(baseX.get() + moveBy);
  });

  return x;
};

export default useVelocityMarquee;
